/**
 * CreatePage - Onboarding Flow for New Carousel
 *
 * This is the original App.tsx onboarding wizard extracted into a dedicated page.
 * Steps: Format → Method → AI Topic → Navigate to Editor
 *
 * Profile is loaded from cai_profiles (set via Instagram Profile Modal on Dashboard).
 * If profile is not set, redirects to Dashboard where the modal will appear.
 *
 * On completion, creates a new project in Supabase and redirects to /editor/:id
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AppStep, CarouselStyle, Profile, Slide, SlideType, AspectRatio, UploadedDocument, Language, AnalisePreCriacao, ChecklistValidacao, AvatarData } from '@/types';
import { MOCK_SLIDES, DEFAULT_AVATAR } from '@/constants';
import { generateCarouselContent, processDocument, formatAvatarForPrompt, TEXT_MODEL_PRO, TEXT_MODEL_FLASH } from '@/services/geminiService';
import { analyzeInput, selectModel, type ModelSelection } from '@/services/modelSelectionService';
import { saveProject } from '@/services/projectService';
import { getProfile, ProfilePreferences } from '@/services/profileService';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { useTranslation } from '@/contexts/LanguageContext';
import AvatarOnboardingModal from '@/components/AvatarOnboardingModal';
import InstagramProfileModal from '@/components/InstagramProfileModal';
import CarouselGenerationLoading from '@/components/CarouselGenerationLoading';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import CoreStudioMark from '@/components/ui/core-studio-mark';
import { cn, generateId } from '@/lib/utils';
import { PROFILE_UPDATED_EVENT } from '@/lib/displayName';
import { extractProjectNameFromCover } from '@/lib/projectName';
import { Sparkles, Wrench, Upload, X, Loader2, Rocket, Sun, Moon, Users, ChevronDown, ChevronUp, Pencil, CheckCircle, AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';

type EditorTheme = 'light' | 'dark';
type GenerationStage = 'analyzing' | 'building' | 'finalizing';

const CreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const { isPaywalled, setShowPaywall, refreshStatus } = useUserStatus();

  // Step state machine
  const [step, setStep] = useState<AppStep>('FORMAT_SELECT');

  // Defense-in-depth: redirect paywalled users back to dashboard
  useEffect(() => {
    if (isPaywalled) {
      navigate('/?paywall=1', { replace: true });
    }
  }, [isPaywalled, navigate]);

  // Allow onboarding tour to set the step via query param
  useEffect(() => {
    const tourStep = searchParams.get('tourStep') as AppStep | null;
    if (tourStep && ['FORMAT_SELECT', 'METHOD_SELECT', 'AI_INPUT'].includes(tourStep)) {
      setStep(tourStep);
    }
  }, [searchParams]);

  // Carousel settings
  const [style, setStyle] = useState<CarouselStyle>(CarouselStyle.TWITTER);
  const [aspectRatio] = useState<AspectRatio>('4/5');
  const [profile, setProfile] = useState<Profile>({
    name: '',
    handle: '',
    avatarUrl: ''
  });
  const [profileLoaded, setProfileLoaded] = useState(false);

  // AI generation settings
  const [aiTopic, setAiTopic] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerationLoading, setShowGenerationLoading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState<GenerationStage>('analyzing');
  const [slideCount, setSlideCount] = useState(7);
  const [selectedModelInfo, setSelectedModelInfo] = useState<ModelSelection | null>(null);

  // Avatar context (Conteúdo Notável methodology)
  const [avatarContext, setAvatarContext] = useState('');
  const [showAvatarContext, setShowAvatarContext] = useState(false);
  const [savedAvatarData, setSavedAvatarData] = useState<AvatarData | null>(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [useOverride, setUseOverride] = useState(false);

  // Document upload state
  const [uploadedDocument, setUploadedDocument] = useState<UploadedDocument | null>(null);
  const [isProcessingDocument, setIsProcessingDocument] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);

  // Profile missing state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);

  const documentInputRef = React.useRef<HTMLInputElement>(null);
  const progressIntervalRef = React.useRef<number | null>(null);

  // Editor theme
  const [editorTheme, setEditorTheme] = useState<EditorTheme>('dark');

  useEffect(() => {
    if (editorTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [editorTheme]);

  const toggleEditorTheme = () => {
    setEditorTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const clearGenerationProgressInterval = () => {
    if (progressIntervalRef.current !== null) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const startGenerationProgress = () => {
    clearGenerationProgressInterval();
    setShowGenerationLoading(true);
    setGenerationProgress(6);
    setGenerationStage('analyzing');

    progressIntervalRef.current = window.setInterval(() => {
      setGenerationProgress((previousProgress) => {
        if (previousProgress >= 99.2) {
          return previousProgress;
        }

        let increment = 0.12;
        if (previousProgress < 25) increment = 1.4;
        else if (previousProgress < 55) increment = 0.9;
        else if (previousProgress < 80) increment = 0.45;
        else if (previousProgress < 92) increment = 0.2;
        else increment = Math.max(0.03, (99.2 - previousProgress) * 0.015);

        const nextProgress = Math.min(99.2, previousProgress + increment);

        if (nextProgress >= 82) setGenerationStage('finalizing');
        else if (nextProgress >= 30) setGenerationStage('building');
        else setGenerationStage('analyzing');

        return nextProgress;
      });
    }, 320);
  };

  // Load profile from cai_profiles on mount
  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (!user) {
          navigate('/', { replace: true });
          return;
        }
        const savedProfile = await getProfile(user.id);

        if (savedProfile && savedProfile.handle && savedProfile.handle.trim()) {
          setProfile({
            name: savedProfile.name,
            handle: savedProfile.handle,
            avatarUrl: savedProfile.avatarUrl,
          });
          setProfileLoaded(true);
          setProfileMissing(false);

          // Auto-load avatar data from preferences
          const prefs: ProfilePreferences | undefined = savedProfile.preferences;
          if (prefs?.avatarData) {
            setSavedAvatarData(prefs.avatarData);
            setAvatarContext(formatAvatarForPrompt(prefs.avatarData));
          }

          // Pre-fill topic from navigation state (e.g., from ContentSuggestions)
          // Gap 1 fix: don't skip to AI_INPUT — let user go through FORMAT_SELECT normally
          // Gap 2 fix: do this AFTER profile is loaded to avoid race conditions
          const prefillTopic = (location.state as any)?.prefillTopic;
          if (prefillTopic && typeof prefillTopic === 'string') {
            setAiTopic(prefillTopic);
          }
        } else {
          setProfileMissing(true);
          setShowProfileModal(true);
        }
      } catch (err) {
        // Failed to load profile - handled by setProfileMissing
        setProfileMissing(true);
        setShowProfileModal(true);
      }
    };

    loadProfile();
  }, [navigate, user]);

  const handleFormatSelect = (selectedStyle: CarouselStyle) => {
    setStyle(selectedStyle);
    setStep('METHOD_SELECT');
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['pdf', 'txt', 'md', 'jpg', 'jpeg', 'png', 'heic'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !validTypes.includes(extension)) {
      setDocumentError(t('create.fileTypeNotSupported'));
      return;
    }

    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setDocumentError(t('create.fileTooLarge'));
      return;
    }

    setIsProcessingDocument(true);
    setDocumentError(null);

    try {
      const doc = await processDocument(file);
      setUploadedDocument(doc);
    } catch (error) {
      // Document processing failed - error shown via setDocumentError
      setDocumentError('Falha ao processar documento. Tente novamente.');
    } finally {
      setIsProcessingDocument(false);
    }
  };

  const handleRemoveDocument = () => {
    setUploadedDocument(null);
    setDocumentError(null);
    if (documentInputRef.current) {
      documentInputRef.current.value = '';
    }
  };

  /**
   * Navigate to editor with slides (manual or AI-generated).
   * Saves project to Supabase and redirects to /editor/:id
   */
  const navigateToEditor = async (
    slides: Slide[],
    analisePreCriacao?: AnalisePreCriacao,
    checklistValidacao?: ChecklistValidacao
  ) => {
    const projectId = generateId();
    const generatedName = extractProjectNameFromCover(slides);
    const locale = language === 'pt-BR' ? 'pt-BR' : language === 'es' ? 'es' : 'en';
    const formattedDate = new Date().toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
    const defaultName = generatedName || t('editor.defaultProjectName', { date: formattedDate });

    // Save to Supabase if user is authenticated
    if (user) {
      try {
        await saveProject({
          id: projectId,
          name: defaultName,
          style,
          aspectRatio,
          profile,
          slides,
          theme: style === CarouselStyle.STORYTELLER ? 'DARK' : 'LIGHT',
          accentColor: '#4763CA',
          showAccent: true,
          showSlideNumbers: true,
          showVerifiedBadge: true,
          headerScale: 1.0,
          fontStyle: 'MODERN',
          fontScale: 1.0,
          globalImageStyle: '',
          nameSource: 'cover_auto',
          language,
          analisePreCriacao,
          checklistValidacao,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }, user.id);
      } catch (err) {
        // Failed to save initial project - non-blocking
      }
    }

    // Navigate to editor with state
    navigate(`/editor/${projectId}`, {
      state: { slides, profile, style, aspectRatio, isNew: true, language, analisePreCriacao, checklistValidacao }
    });
  };

  const handleManualCreate = () => {
    const slides: Slide[] = [
      { id: '1', type: SlideType.COVER, content: 'Your Hook Here', showImage: false },
      { id: '2', type: SlideType.CONTENT, content: 'Your content goes here.', showImage: true },
      { id: '3', type: SlideType.CTA, content: 'Link in bio.', showImage: false },
    ];
    navigateToEditor(slides);
  };

  // Auto-select model based on input type (real-time)
  useEffect(() => {
    if (!aiTopic.trim() && !uploadedDocument) {
      setSelectedModelInfo(null);
      return;
    }

    const analysis = analyzeInput(aiTopic, uploadedDocument);
    const selection = selectModel(analysis);
    setSelectedModelInfo(selection);
  }, [aiTopic, uploadedDocument]);

  useEffect(() => {
    return () => clearGenerationProgressInterval();
  }, []);

  const handleAiGenerate = async () => {
    if (!aiTopic.trim() && !uploadedDocument) return;

    // Automatically select the best model for this input
    const analysis = analyzeInput(aiTopic, uploadedDocument);
    const modelSelection = selectModel(analysis);
    const selectedModel = modelSelection.model;

    setIsGenerating(true);
    startGenerationProgress();
    try {
      const result = await generateCarouselContent(
        aiTopic,
        slideCount,
        selectedModel,
        uploadedDocument || undefined,
        language,
        avatarContext || undefined
      );
      setUploadedDocument(null);
      setGenerationStage('finalizing');
      setGenerationProgress(100);
      clearGenerationProgressInterval();
      await new Promise((resolve) => setTimeout(resolve, 220));
      setShowGenerationLoading(false);

      navigateToEditor(result.slides, result.analisePreCriacao, result.checklistValidacao);
    } catch (error: any) {
      // If the error is PAYWALL_REQUIRED, show paywall modal instead of raw alert
      const errorMessage = error?.message || '';
      if (errorMessage.includes('PAYWALL_REQUIRED') || errorMessage.includes('LIMIT_EXCEEDED')) {
        setShowPaywall(true);
        return;
      }
      alert(t('create.aiGenerationFailed', { detail: errorMessage || t('errors.unknown') }));
    } finally {
      clearGenerationProgressInterval();
      setShowGenerationLoading(false);
      setGenerationProgress(0);
      setGenerationStage('analyzing');
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-xl w-full overflow-hidden">
        <CardHeader className="bg-card border-b text-center relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleEditorTheme}
            className="absolute right-4 top-4"
            title={editorTheme === 'light' ? t('create.darkMode') : t('create.lightMode')}
          >
            {editorTheme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </Button>
          <div className="flex justify-center mb-2">
            <CoreStudioMark size={40} />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">{t('create.coreStudio')}</CardTitle>
          <CardDescription>{t('create.coreStudioTagline')}</CardDescription>
        </CardHeader>

        <CardContent className="p-4 md:p-8">
          {/* 1. Format Selection */}
          {step === 'FORMAT_SELECT' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-center">{t('create.chooseStyle')}</h2>
              <div data-tour="create-style-cards" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button onClick={() => handleFormatSelect(CarouselStyle.TWITTER)} className="p-6 border-2 border-primary/50 bg-primary/5 rounded-xl text-left hover:shadow-lg hover:border-primary transition-all group">
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center mb-4 text-xl font-bold">&#x1d54f;</div>
                  <h3 className="font-bold group-hover:text-primary">{t('create.twitterStyle')}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t('create.twitterDesc')}</p>
                </button>
                <button onClick={() => handleFormatSelect(CarouselStyle.STORYTELLER)} className="p-6 border-2 border-muted bg-muted/30 rounded-xl text-left hover:shadow-lg hover:border-primary/50 transition-all group">
                  <div className="w-10 h-10 bg-muted text-foreground rounded-full flex items-center justify-center mb-4 text-xl font-bold">&#x1f4f7;</div>
                  <h3 className="font-bold group-hover:text-primary">{t('create.storyteller')}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{t('create.storytellerDesc')}</p>
                </button>
              </div>
              <Button variant="ghost" onClick={() => navigate('/')} className="w-full">{t('create.backToDashboard')}</Button>
            </div>
          )}

          {/* 2. Method Selection */}
          {step === 'METHOD_SELECT' && (
            <div className="space-y-6 animate-fade-in">
              <h2 className="text-xl font-semibold text-center">{t('create.howToStart')}</h2>
              <div data-tour="create-method-cards" className="grid grid-cols-1 gap-4">
                <button onClick={() => setStep('AI_INPUT')} className="relative overflow-hidden p-6 border-2 border-primary/30 bg-primary/5 rounded-xl text-left hover:shadow-md hover:border-primary/50 transition-all group">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <h3 className="font-bold text-lg">{t('create.useAI')}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('create.useAIDesc')}</p>
                </button>
                <button onClick={handleManualCreate} className="p-6 border-2 border-border bg-card rounded-xl text-left hover:border-muted-foreground/30 transition-all">
                  <div className="flex items-center gap-2 mb-1">
                    <Wrench className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-bold text-lg">{t('create.manualCreate')}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{t('create.manualCreateDesc')}</p>
                </button>
              </div>
              <Button variant="ghost" onClick={() => setStep('FORMAT_SELECT')} className="w-full">{t('create.back')}</Button>
            </div>
          )}

          {/* 3. AI Input */}
          {step === 'AI_INPUT' && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center">
                <h2 className="text-xl font-semibold">{t('create.whatIsYourPost')}</h2>
                <p className="text-sm text-muted-foreground mt-1">{t('create.whatIsYourPostDesc')}</p>
              </div>
              <Textarea data-tour="create-ai-input" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} className="h-28 resize-none" placeholder={t('create.topicPlaceholder')} />

              {/* Avatar / Target Audience Section (Conteúdo Notável) */}
              {savedAvatarData ? (
                /* Avatar saved — show compact card with magnetic bio + structured data */
                <div className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{t('create.targetAudience')}</span>
                      {savedAvatarData.magneticBio ? (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/30">{t('create.magneticBio')}</span>
                      ) : (
                        <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full border border-green-500/30">{t('create.saved')}</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setShowAvatarModal(true)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      {t('create.edit')}
                    </Button>
                  </div>

                  {/* Display magnetic bio or fallback to bioDescription */}
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {savedAvatarData.magneticBio || savedAvatarData.bioDescription || (
                      <>
                        <span className="font-medium text-foreground">{savedAvatarData.nicho}</span>
                        {savedAvatarData.demografico && <> — {savedAvatarData.demografico}</>}
                      </>
                    )}
                  </p>

                  {/* Show structured tags if available */}
                  {(savedAvatarData.publicoAlvo || savedAvatarData.doresIdentificadas?.length || savedAvatarData.temasConteudo?.length) && (
                    <div className="flex flex-wrap gap-1.5">
                      {savedAvatarData.publicoAlvo && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          {savedAvatarData.publicoAlvo}
                        </span>
                      )}
                      {savedAvatarData.doresIdentificadas?.slice(0, 3).map((dor, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                          {dor}
                        </span>
                      ))}
                      {(savedAvatarData.doresIdentificadas?.length || 0) > 3 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                          +{(savedAvatarData.doresIdentificadas?.length || 0) - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Override toggle */}
                  <button
                    type="button"
                    onClick={() => {
                      setUseOverride(!useOverride);
                      if (useOverride) {
                        // Turning off override — restore saved avatar context
                        setAvatarContext(formatAvatarForPrompt(savedAvatarData));
                      }
                    }}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {useOverride ? <ToggleRight className="h-4 w-4 text-primary" /> : <ToggleLeft className="h-4 w-4" />}
                    <span>{t('create.useDifferentAudience')}</span>
                  </button>

                  {useOverride && (
                    <Textarea
                      value={avatarContext}
                      onChange={(e) => setAvatarContext(e.target.value)}
                      className="h-28 resize-none text-sm"
                      placeholder={t('create.audiencePlaceholder')}
                    />
                  )}
                </div>
              ) : (
                /* No avatar saved — show prompt to configure */
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 bg-orange-500/5 border-b border-border">
                    <AlertCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      <span className="text-foreground font-medium">{t('create.audienceNotSet')}</span> {t('create.audienceNotSetDesc')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs flex-shrink-0 ml-auto"
                      onClick={() => setShowAvatarModal(true)}
                    >
                      <Users className="h-3 w-3 mr-1" />
                      {t('create.define')}
                    </Button>
                  </div>

                  {/* Fallback textarea */}
                  <button
                    type="button"
                    onClick={() => setShowAvatarContext(!showAvatarContext)}
                    className="w-full flex items-center justify-between px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
                  >
                    <span>{t('create.orDescribeManually')}</span>
                    {showAvatarContext ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                  {showAvatarContext && (
                    <div className="px-4 pb-4 border-t border-border">
                      <Textarea
                        value={avatarContext}
                        onChange={(e) => setAvatarContext(e.target.value)}
                        className="h-28 resize-none text-sm mt-3"
                        placeholder={`Meu nome é [seu nome], [formação/especialista] em [sua área]. Falo sobre [tema 1], [tema 2] e [tema 3]. E ajudo [tipo de pessoa] a [resultado desejado].`}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Document Upload */}
              <div data-tour="create-upload" className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                {uploadedDocument ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{uploadedDocument.type === 'pdf' ? '📄' : uploadedDocument.type === 'image' ? '🖼️' : '📃'}</span>
                      <div className="text-left">
                        <p className="text-sm font-medium truncate max-w-[200px]">{uploadedDocument.name}</p>
                        <p className="text-xs text-muted-foreground">{(uploadedDocument.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={handleRemoveDocument} className="text-muted-foreground hover:text-destructive">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : isProcessingDocument ? (
                  <div className="flex items-center justify-center gap-2 py-2">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">{t('create.processingDocument')}</span>
                  </div>
                ) : (
                  <label className="cursor-pointer block py-2">
                    <input type="file" ref={documentInputRef} className="hidden" accept=".pdf,.txt,.md,.jpg,.jpeg,.png,.heic" onChange={handleDocumentUpload} />
                    <Upload className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                    <span className="text-primary font-medium">{t('create.documentUpload')}</span>
                    <span className="text-muted-foreground text-sm ml-1">(PDF, TXT, MD, JPG, PNG, HEIC)</span>
                  </label>
                )}
              </div>
              {documentError && <p className="text-destructive text-sm text-center">{documentError}</p>}

              {/* Controls */}
              <div data-tour="create-slide-count">
                <Label className="text-xs uppercase mb-2 block">{t('create.slidesCount')}</Label>
                <div className="flex items-center gap-3 bg-muted/50 p-3 rounded-md border border-border">
                  <Slider value={[slideCount]} onValueChange={(value) => setSlideCount(value[0])} min={3} max={15} step={1} className="flex-1" />
                  <span className="w-6 text-center font-bold text-sm">{slideCount}</span>
                </div>
              </div>

              <Button onClick={handleAiGenerate} disabled={isGenerating || (!aiTopic.trim() && !uploadedDocument)} className="w-full h-12" size="lg">
                {isGenerating ? (
                  <><Loader2 className="h-5 w-5 animate-spin mr-2" />{t('create.generating')}</>
                ) : (
                  <><Rocket className="h-5 w-5 mr-2" />{t('create.generate')}</>
                )}
              </Button>
              {!isGenerating && <Button variant="ghost" onClick={() => setStep('METHOD_SELECT')} className="w-full">{t('create.back')}</Button>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Avatar Onboarding Modal */}
      <AvatarOnboardingModal
        open={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onAvatarSaved={(avatar) => {
          setSavedAvatarData(avatar);
          setAvatarContext(formatAvatarForPrompt(avatar));
          setUseOverride(false);
        }}
        userId={user?.id || ''}
        initialData={savedAvatarData || undefined}
      />

      {/* Instagram Profile Modal - shown when profile is missing */}
      <InstagramProfileModal
        open={showProfileModal}
        onClose={() => {
          setShowProfileModal(false);
          // If profile still missing after closing modal, go back to dashboard
          if (profileMissing) {
            navigate('/', { replace: true });
          }
        }}
        onProfileSaved={(savedProfile) => {
          setProfile({
            name: savedProfile.name,
            handle: savedProfile.handle,
            avatarUrl: savedProfile.avatarUrl,
          });
          window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
          setProfileLoaded(true);
          setProfileMissing(false);
          setShowProfileModal(false);
        }}
        required={profileMissing}
        userId={user?.id || ''}
      />

      <CarouselGenerationLoading
        open={showGenerationLoading}
        progress={generationProgress}
        stage={generationStage}
      />
    </div>
  );
};

export default CreatePage;
